/**
 * The signed-in player's coin balance, for the header on every page.
 *
 * Before this, only /me, /me/coins and the home page's daily panel ever read `/api/coins/balance`, so every other page
 * showed the store's initial 0 until one of those had been visited. The query is keyed by the account id, so signing in,
 * out or switching accounts never shows another actor's balance.
 */
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useCoinStore } from '@/stores/coinStore'
import { app } from './client'

export const getCoinBalance = async (): Promise<number> => {
  const res = await app.get<{ balance: number }>('/coins/balance')
  if (typeof res?.balance !== 'number') throw new Error('coin balance missing from response')
  return res.balance
}

export const coinBalanceKey = (accountId: string | null | undefined) => ['coins', 'balance', accountId ?? 'guest'] as const

/** Keep the shared coin store in step with the server for `accountId` (no-op while signed out). */
export function useCoinBalanceSync(accountId: string | null | undefined): void {
  const setBalance = useCoinStore((s) => s.setBalance)
  const setUnavailable = useCoinStore((s) => s.setUnavailable)
  const q = useQuery({
    queryKey: coinBalanceKey(accountId),
    queryFn: getCoinBalance,
    enabled: !!accountId,
    retry: 1,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
  useEffect(() => {
    if (q.data !== undefined) setBalance(q.data)
  }, [q.data, q.dataUpdatedAt, setBalance])
  useEffect(() => {
    if (q.isError) setUnavailable()
  }, [q.isError, q.errorUpdatedAt, setUnavailable])
}
